/**
 * Location Name Search
 *
 * Client-side matching of pickup locations by name.
 *
 * @remarks
 * The map search box is wired to the Jawg Places API, which only knows
 * localities (city / postcode / county) — it cannot match a pickup location
 * by its name (e.g. "The Thirsty Goat"). Location names and coordinates are
 * already loaded in the browser to render the list, so matching a name is a
 * cheap in-memory filter, no geocoder involved. The result feeds the search
 * dropdown alongside Jawg's locality suggestions.
 */

import type { Location } from '@/types/location';

/**
 * Maximum number of name-matched locations to surface in the dropdown,
 * to keep the suggestion list readable alongside Jawg's locality results.
 */
export const MAX_LOCATION_MATCHES = 5;

/**
 * Normalize text for matching: lowercase, strip diacritics, replace any
 * punctuation with a space, and collapse whitespace.
 *
 * @remarks
 * Apostrophes are removed entirely, so "Jabos" matches "Jabo's". Other
 * punctuation — periods, commas, parentheses — becomes a token boundary, so
 * "Lowe's Indianapolis (Michigan Rd)" tokenizes to lowes / indianapolis /
 * michigan / rd. This keeps matching punctuation-insensitive.
 */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '') // strip diacritic marks (cafe vs café)
    .replace(/['‘’]/g, '') // drop apostrophes so Jabos == Jabo's
    .replace(/[^a-z0-9\s]/g, ' ') // other punctuation -> word boundary
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Match locations by name against a free-text query.
 *
 * Tokenizes the normalized query and keeps locations whose normalized name
 * contains every token (in any order), so multi-word and punctuation-bearing
 * queries match. Results are ranked so names that *start with* the full query
 * come first, then alphabetically. Returns at most `limit` matches.
 *
 * Normalization handles punctuation and diacritics; it does not do
 * edit-distance (typo) matching — that is a deliberate later step.
 *
 * @param locations - All loaded locations to search
 * @param query - The user's search text
 * @param limit - Maximum matches to return (defaults to MAX_LOCATION_MATCHES)
 * @returns Matching locations, best matches first
 */
export function matchLocationsByName(
  locations: Location[],
  query: string,
  limit: number = MAX_LOCATION_MATCHES,
): Location[] {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) {
    return [];
  }

  const tokens = normalizedQuery.split(' ');

  const matches = locations.filter((location) => {
    const normalizedName = normalize(location.name);
    return tokens.every((token) => normalizedName.includes(token));
  });

  matches.sort((a, b) => {
    const aStartsWith = normalize(a.name).startsWith(normalizedQuery);
    const bStartsWith = normalize(b.name).startsWith(normalizedQuery);
    if (aStartsWith !== bStartsWith) {
      return aStartsWith ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  return matches.slice(0, limit);
}
